export type RecordOfAny = Record<string, any>

export interface Profile {
  id: number
  email: string
  firstName: string
  lastName: string
  ssn: string
  birthDate: string
  phoneNumber: string
  lastSignInAt: string
  medicalDocuments: MedicalDocument[]
  isAcademy: boolean
  isMaster: boolean
  isControl: boolean
  isConsultation: boolean
  isTadalafila: string
  isPrep: boolean | null
  hasPrepTreatmentForm: boolean
  sentTimeRecommendationAt: string
  sentMasterRecommendationAt: string
  firstConsultationOrderNumber: string
  preDiag: PreDiag
  psychologyCredits: number
  recommendedPsychologyPackage: string
  recommendedUrologistToPartner: boolean
  submittedPartnerEmail: boolean
  canSeePrepSidebar: boolean
  userId: number
  address: Address
  doctor: ProfileDoctor
  pharmacist: Pharmacist
  creditCards: CreditCard[]
  subscriptions: Subscription[]
  lastConsultationSpeciality?: string
  activeTreatmentSpecialties?: string[]
  lastPrescriptionDoctor?: ProfileDoctor
  validPrescriptions?: ProfileValidPrescriptions
  pathologies?: { [key: string]: string[] }
  weightCredits?: {
    endocrinologyCredits: number | null
    personalTrainerCredits: number | null
    nutritionistCredits: number | null
  }
  wHeight?: number
  age?: number | null
  isWeight?: boolean
  isWeightOnly?: boolean
  isFirstEndocrinologistConsultation?: boolean
  needsWeightSurvey?: boolean
  rgNumber?: string | null
  cpfNumber?: string | null
  zipCode?: string | null
  streetName?: string | null
  streetNumber?: string | null
  endocrinologistFromLastConsultation?: string | null
  patientCredits?: number
  gender?: string | null
  prescriptionSex?: 'M' | 'F' | null
  weightLossGoal: number | null
  finalWeightLossGoal: number | null
  partnerName: string | null
  lillyRegistrationConfirmedAt?: string | null
  referralSource?: string | null
  medleveServiceRecommendations?: { nutritionist: boolean; personal_trainer: boolean; psychologist: boolean }
}

type ProfileValidPrescriptions = {
  validPrescriptions?: number
  allPrescriptions?: number
} & Record<string, number>

export interface Address {
  id: number
  firstname: string
  lastname: string
  address1: string
  address2: string
  city: string
  zipcode: string
  phone: string
  stateName: string
  neighborhood?: string | null
  alternativePhone: null
  company: null
  countryName: string
  countryIso3: string
  countryIso: string
  createdAt: string
  updatedAt: string
}

export interface CreditCard {
  id: number
  ccType: null | string
  lastDigits: null | string
  year: number | null
  month: number | null
  iuguCustomerId: null | string
}

export type ProfileDoctor = Pick<Doctor, 'fullName' | 'avatarUrl' | 'userId'>

export interface MedicalDocument {
  id: number
  filename: string
  url: string
  createdAt: string
  awsPhoto: boolean
}

// Medleve Typeform Questionnaires
export interface QuestionnaireAnswer {
  field?: {
    id: string
    type: string
    ref: string
  }
  field_id?: string
  field_ref?: string
  question: string
  type: string
  text?: string
  email?: string
  number?: number
  boolean?: boolean
  date?: string
  choice?: {
    label: string
    other?: string
  }
  choices?: Array<{
    label: string
    other?: string
  }>
  fileUrl?: string
  url?: string
}

export interface MedleveQuestionnaire {
  id: number
  patientId: number
  typeformFormId: string
  typeformResponseId: string
  submittedAt: string
  type: 'initial' | 'progression'
  number: number
  label: string
  answers: QuestionnaireAnswer[]
  createdAt: string
  updatedAt: string
}

export interface NextFormResponse {
  success: boolean
  typeformId: string
  consultationCount: number
  isFirstConsultation: boolean
}

export interface QuestionnairesListResponse {
  success: boolean
  questionnaires: MedleveQuestionnaire[]
  count: number
}

export interface SyncQuestionnairesResponse {
  success: boolean
  synced: number
  message: string
}

export interface Pharmacist {
  name: string
  userId: number
}

export interface PreDiag {
  uuid: string
  height: number
  weight: number
  imc: number
  allergies: string
  history: string[]
  surveySubmission: RecordOfAny
  isComplete: boolean
}

export interface Survey extends Record<string, any> {
  locale: string
  logoPosition: string
  completedHTML: string
  pages: SurveyPage[]
  pagePrevText: string
  pageNextText: string
}

export interface SurveyPage {
  name: string
  elements: SurveyElement[]
  visibleIf?: string
}

export interface SurveyElement {
  type: SurveyElementType
  name: string
  title:
    | {
        default: string
        pt: string
      }
    | string
  isRequired?: boolean
  choices?: {
    value: string
    text: string
    enableIf?: string
  }[]
  showOtherItem?: boolean
  otherText?: string
  validators?: {
    type: string
  }[]
  inputType?: string
  placeholder?: string
  visibleIf?: string
  rateValues?: {
    value: string
    text: string
  }[]
  rateMax?: number
}

export enum SurveyElementType {
  Checkbox = 'checkbox',
  Comment = 'comment',
  Radiogroup = 'radiogroup',
  Rating = 'rating',
  Text = 'text'
}

export interface Subscription {
  planType: string
  firstPaymentAt: string
  nextPaymentAt: null
  price: number
  medName: string
  subscriptionId: number
}

export interface Doctor {
  id: number
  userId: number
  fullName: string
  freeTimeSlots: string[]
  freeHalfHourSlots: string[]
  freeQuarterHourSlots: string[]
  nextFreeTimeSlot: null | string
  nextFreeSimpleTimeSlot: null | string
  nextFreeComplexTimeSlot: null | string
  communicationChannels: DoctorCommunicationChannel[]
  consultationTypes: string[]
  avatarURL: string
  speciality: string
  z: number
  openConsultationSlug: string
  consultationSpecialties: DoctorConsultationSpecialty[]
  description: string
  personalWebsiteURL: string
  isPsychology: boolean
  cdomNumber: string
  openConsultationVariants: any[]
  avatarUrl?: string
  personalWebsiteUrl?: string
  /** Virtual item prepended by the backend for live (waiting-line) consultations */
  live?: boolean
}

export enum DoctorCommunicationChannel {
  Chat = 'chat',
  Phone = 'phone',
  Visio = 'visio'
}

export interface DoctorConsultationSpecialty {
  id: number
  slug: string
  createdAt: string
  updatedAt: string
}

export type ConsultationState =
  | 'confirmed'
  | 'started'
  | 'delivered'
  | 'waiting'
  | 'paid'
  | 'canceled'
  | 'finished'
  | 'confirm'
  | 'complete'
  | 'ready'
  | 'shipped'

export type Consultation = {
  finishedAt?: string | null
  nutritionOfferEligible?: boolean
  id: number
  communicationChannel?: DoctorCommunicationChannel
  questionnairePending?: boolean
  questionnaireUnavailable?: boolean
  completedSurvey?: boolean
  consultationType?: string
  consultationPrice?: number
  patientAttributes?: object
  doctor?: string
  doctorAvatarUrl?: string
  doctorId: number
  appointmentAt: string
  pdid?: number
  total?: number
  number?: string
  orderNumber?: string
  patientInfoToken?: string
  cuideiOnboardingComplete?: boolean
  orderId?: number
  orderState?: string
  surveyId?: number
  doctorUserId?: number
  surveySubmission?: object
  success?: boolean
  state?: ConsultationState
  visioSession?: VisioSession
  userId?: number
  reportUrl?: string
  onCreateSuccess?: () => void
  gatewaySecureModeRedirectUrl?: string
  canBeFollowedUp?: boolean
  recommendedTime?: boolean
  recommendedMaster?: boolean
  recommendedPsychology?: boolean
  prematureEjaculation?: boolean
  psychologyRecommendationStatus?: string
  packageType?: string
  speciality?: string
  fullDoctor?: Doctor
  billAddress?: any
  needsPreDiag?: boolean
  preDiagUuid?: string
  preDiagTypeformId?: string
  isMedleve?: boolean
  isIsha?: boolean
  isCuidei?: boolean
  cuideiPharmacyId?: number | null
  patientAuthorizedDocumentDelivery?: boolean
  referralSource?: string | null
  needsTypeformPrediag?: boolean
  /** Backend-authoritative readiness for Medleve/Isha live doctor queue. */
  livePrediagReady?: boolean
  creditCards?: CreditCard[]
  patientId: number
  paymentMethodId: number
  erectileDysfunctionOrientation: boolean | null
  prematureEjactulationOrientation: boolean | null
  medicationOrientation: boolean | null
  /** True when the consultation was created as a live (waiting-line) consultation */
  isLive?: boolean
  /** True for the R$89 fast prescription-renewal flow. */
  isPrescriptionRenewal?: boolean
}

export type PrescriptionType =
  | 'erection_drugs'
  | 'analysis'
  | 'analysis_prep'
  | 'analysis_prep_mandatory'
  | 'analysis_prep_anual'
  | 'daily_prep'
  | 'on_demand_prep'
  | 'daily_prep_drugs'
  | 'on_demand_prep_drugs'
  | 'phytotherapy'
  | 'compounding'
  | 'other'
  | 'omens_fit_initial'
  | 'omens_fit_initial_women'
  | 'omens_fit_imaging'
  | 'omens_fit_imaging_with_densitometry'
  | 'weight_loss'

export interface Prescription {
  canBeReleased: boolean
  canBeDownloaded: boolean
  createdAt: string
  doctor: string
  downloadUrl: string
  expiredAt: string
  id: number
  uuid: string
  prescriptionType: PrescriptionType
  prescriptionItems: PrescriptionItem[]
  renewability: string
  unlockingOrderNumber?: number
  paymentMethodId?: number
  wasDelivered: boolean
  'isControlled?'?: boolean
  'purchasable?'?: boolean
  'draft?'?: boolean
  deliverOrderNumber?: number | null
  deliverOrderState?: string | null
  unlockingOrderState?: string | null
  explainedDuringConsultation?: boolean
  isControlled?: boolean
  purchasable?: boolean
  draft?: boolean
  consultationSpecialty?: string
  consultationId: number
  alreadyPurchased?: boolean
  wegovyPurchaseBlocked?: boolean
  // Latest paid purchase that used this prescription (null when never used).
  usedAt?: string | null
  usedOrderNumber?: string | null
  // Description-only prescriptions ('other') carry the medicine as free text.
  description?: string | null
}

export type PrescriptionItem = {
  molecule: string
  grammage: number
  dosage: string
}

export type VisioSession = {
  session: {
    apiKey: string
    sessionId: string
  }
  tokens: {
    doctor: string
    patient: string
  }
}

export type ConsultationMessageUser = Pick<Profile, 'id' | 'email' | 'lastSignInAt' | 'address'>

export interface ConsultationMessageReceipt {
  isDelivered: boolean
  isRead: boolean
  receiver: ConsultationMessageUser
}

export interface ConsultationMessage {
  id: number
  type: string
  body: string
  createdAt: string
  sender: ConsultationMessageUser
  receipts: ConsultationMessageReceipt[]
}

export type GoogleItem = {
  name: string
  variant: string
  category: string
  quantity: number
  price: string
}

export type ReducerActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key
      }
    : {
        type: Key
        payload: M[Key]
      }
}

declare global {
  interface Window {
    Intercom: (action: string, scope: string, data: any) => void
    gtag: (param: string, value: string | undefined, data?: object | undefined) => void
    fbq: (param: string, value: string | undefined, data?: object | undefined) => void
  }
}

export type Pathology =
  | 'acne'
  | 'dermatitis'
  | 'dermatology_other'
  | 'endocrinology'
  | 'erectile_dysfunction'
  | 'hair_loss'
  | 'hpb'
  | 'libido'
  | 'nutrition'
  | 'ozempic'
  | 'other'
  | 'penis_enlargement'
  | 'personal_training'
  | 'premature_ejaculation'
  | 'prep'
  | 'prep_private'
  | 'psychology'
  | 'weight_loss'

export type PatologiaCadastroGtm = 'phyto' | 'sex_shop' | '+lib' | '+fert' | '+hair' | 'not_found'
